import React from 'react';

import { QRCode } from 'antd';
import { AlertCircle, CheckCircle2, Clock3, RefreshCw, XCircle } from 'lucide-react';

import Modal from '@/components/common/Modal';
import type { PaymentCreateOrderResponse, PaymentOrderStatus } from '@/types';

import type { PaymentChannelAction } from '../utils/paymentChannelPayload';

interface PointsPurchaseModalProps {
  open: boolean;
  order: PaymentCreateOrderResponse;
  status: PaymentOrderStatus | null;
  refreshing: boolean;
  error: string | null;
  channelAction: PaymentChannelAction;
  onRefresh: () => void;
  onClose: () => void;
}

const formatAmount = (amountCent: number): string => `¥${(amountCent / 100).toFixed(2)}`;
const formatPoints = (points: number): string => `${points}积分`;

const ALIPAY_PAYMENT_EXPIRE_MS = 150 * 60 * 1000;

const formatCountdown = (remainingMs: number, options: { showHours?: boolean } = {}): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  if (options.showHours) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
      seconds,
    ).padStart(2, '0')}`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const parseExpiresAt = (value?: string | null): number | null => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const isAwaitingPayment = (status: PaymentOrderStatus | null): boolean =>
  !status?.pay_status || status.pay_status === 'PENDING' || status.pay_status === 'PAYING';

const isAlipayPayment = (order: PaymentCreateOrderResponse): boolean => {
  const methodId = order.payment_method.method_id.toLowerCase();
  const methodName = order.payment_method.name.toLowerCase();
  return (
    methodId.includes('alipay') ||
    methodName.includes('alipay') ||
    order.payment_method.name.includes('支付宝')
  );
};

const resolveStatus = (
  status: PaymentOrderStatus | null,
  channelAction: PaymentChannelAction,
  options: { expired: boolean },
) => {
  if (options.expired) {
    return {
      title: '支付超时',
      description:
        channelAction.type === 'qr'
          ? '二维码已失效，请重新发起购买'
          : '支付页面已超时，请重新发起购买',
      icon: XCircle,
      tone: 'error',
    };
  }
  if (status?.pay_status === 'PAID' && status.credit_status === 'SUCCESS') {
    return {
      title: '支付成功',
      description: '积分已到账，可在积分明细中查看流水',
      icon: CheckCircle2,
      tone: 'success',
    };
  }
  if (status?.pay_status === 'PAID' && status.credit_status === 'FAILED') {
    return {
      title: '已支付，积分到账失败',
      description: '支付已确认，但积分到账失败，请联系客服处理',
      icon: AlertCircle,
      tone: 'error',
    };
  }
  if (status?.pay_status === 'PAID') {
    return {
      title: '已支付，到账处理中',
      description: '支付已确认，积分到账处理中，请稍后刷新',
      icon: AlertCircle,
      tone: 'warning',
    };
  }
  if (status?.pay_status === 'FAILED' || status?.pay_status === 'CLOSED') {
    return {
      title: '支付失败',
      description: '支付未完成或已关闭，请重新发起购买',
      icon: XCircle,
      tone: 'error',
    };
  }
  return {
    title: '待支付',
    description:
      channelAction.type === 'qr'
        ? '请使用微信扫描二维码完成付款，完成后刷新状态'
        : '请在新打开的支付页面完成付款，超时未支付订单将自动关闭',
    icon: Clock3,
    tone: 'pending',
  };
};

const PointsPurchaseModal: React.FC<PointsPurchaseModalProps> = ({
  open,
  order,
  status,
  refreshing,
  error,
  channelAction,
  onRefresh,
  onClose,
}) => {
  const alipayPayment = isAlipayPayment(order);
  const [localAlipayExpiresAt, setLocalAlipayExpiresAt] = React.useState<{
    orderId: string;
    expiresAtMs: number;
  } | null>(null);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const explicitExpiresAtMs = parseExpiresAt(order.expires_at ?? status?.expires_at ?? null);
  const awaitingPayment = isAwaitingPayment(status);
  const alipayExpiresAtMs = alipayPayment
    ? localAlipayExpiresAt?.orderId === order.order_id
      ? localAlipayExpiresAt.expiresAtMs
      : null
    : null;
  const expiresAtMs = alipayPayment ? alipayExpiresAtMs : explicitExpiresAtMs;

  React.useEffect(() => {
    if (!open || !alipayPayment) return;
    const startedAtMs = Date.now();
    setNowMs(startedAtMs);
    setLocalAlipayExpiresAt((current) => {
      if (current?.orderId === order.order_id) return current;
      return {
        orderId: order.order_id,
        expiresAtMs: startedAtMs + ALIPAY_PAYMENT_EXPIRE_MS,
      };
    });
  }, [alipayPayment, open, order.order_id]);

  React.useEffect(() => {
    if (!open || !expiresAtMs || !awaitingPayment) return undefined;
    setNowMs(Date.now());
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [awaitingPayment, expiresAtMs, open]);

  const remainingMs = expiresAtMs === null ? null : expiresAtMs - nowMs;
  const paymentExpired =
    awaitingPayment &&
    remainingMs !== null &&
    remainingMs <= 0 &&
    (channelAction.type === 'qr' || alipayPayment);
  const qrExpired = channelAction.type === 'qr' && paymentExpired;
  const alipayExpired = alipayPayment && paymentExpired;
  const countdown =
    channelAction.type === 'qr' && awaitingPayment && remainingMs !== null && remainingMs > 0
      ? formatCountdown(remainingMs)
      : null;
  const alipayCountdown =
    alipayPayment && awaitingPayment && remainingMs !== null && remainingMs > 0
      ? formatCountdown(remainingMs, { showHours: true })
      : null;
  const current = resolveStatus(status, channelAction, {
    expired: paymentExpired,
  });
  const StatusIcon = current.icon;
  const iconClassName =
    current.tone === 'success'
      ? 'bg-emerald-500/15 text-emerald-400'
      : current.tone === 'warning'
        ? 'bg-amber-500/15 text-amber-400'
        : current.tone === 'error'
          ? 'bg-red-500/15 text-red-400'
          : 'bg-theme-color-10 text-theme-color';

  return (
    <Modal
      title="积分支付"
      description="ManClaw 积分充值"
      open={open}
      onClose={onClose}
      width="min(520px, calc(100vw - 48px))"
    >
      <div className="space-y-4">
        <section className="rounded-lg border border-dark-border bg-dark-card p-5">
          <div className="flex items-start gap-3">
            <span
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}
            >
              <StatusIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-dark-text">{current.title}</h3>
                {(alipayPayment && (alipayCountdown || alipayExpired)) ||
                (channelAction.type === 'qr' && (countdown || qrExpired)) ? (
                  <span
                    className={`inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums ${
                      alipayExpired || qrExpired
                        ? 'bg-red-500/15 text-red-300'
                        : 'bg-theme-color-10 text-theme-color'
                    }`}
                  >
                    {alipayExpired || qrExpired
                      ? '已超时'
                      : `剩 ${alipayPayment ? alipayCountdown : countdown}`}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-dark-text-secondary">{current.description}</p>
            </div>
          </div>
          {error ? (
            <div className="mt-4 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          ) : null}
        </section>

        {channelAction.type === 'qr' ? (
          <section className="rounded-lg border border-dark-border bg-dark-card p-4">
            <div className="mb-3 text-center text-sm font-medium text-dark-text">微信扫码支付</div>
            <div className="relative mx-auto w-fit">
              <div
                data-testid="wechat-payment-qrcode-frame"
                className={`flex rounded-lg bg-white p-3 transition duration-200 ${
                  qrExpired ? 'pointer-events-none blur-sm grayscale opacity-35' : ''
                }`}
              >
                <QRCode
                  type="svg"
                  value={channelAction.value}
                  size={220}
                  bordered={false}
                  color="#000000"
                  bgColor="#ffffff"
                  data-testid="wechat-payment-qrcode"
                />
              </div>
              {qrExpired ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-dark-bg/80 px-4 text-center text-dark-text">
                  <XCircle className="mb-2 h-7 w-7 text-red-400" />
                  <span className="text-sm font-semibold">二维码已失效</span>
                  <span className="mt-1 text-xs text-dark-text-secondary">请重新发起购买</span>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="rounded-lg border border-dark-border bg-dark-card p-4">
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <dt className="text-dark-text-secondary">订单金额</dt>
              <dd className="font-semibold tabular-nums text-dark-text">
                {formatAmount(order.amount_cent)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-dark-text-secondary">到账积分</dt>
              <dd className="font-semibold tabular-nums text-dark-text">
                {formatPoints(order.points)}
              </dd>
            </div>
          </dl>
        </section>

        <button
          type="button"
          aria-label="刷新支付状态"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-theme-color px-4 text-sm font-medium text-white transition-colors hover:bg-theme-color/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? '刷新中' : '刷新状态'}
        </button>
      </div>
    </Modal>
  );
};

export default PointsPurchaseModal;
